import React, { useEffect, useState } from "react";
import { useAuth } from "@/admin/AuthContext";
import { Loader2, ScrollText, ShieldCheck, RefreshCw, User as UserIcon, PlusCircle, PencilLine, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ACTION_META = {
  "user.create": {
    label: "Buat User",
    icon: PlusCircle,
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  "user.update": {
    label: "Update User",
    icon: PencilLine,
    className: "bg-amber-50 text-amber-700 border-amber-200",
  },
  "user.delete": {
    label: "Hapus User",
    icon: Trash2,
    className: "bg-red-50 text-red-700 border-red-200",
  },
};

const formatChanges = (changes) => {
  if (!changes || typeof changes !== "object") return null;
  const entries = Object.entries(changes);
  if (entries.length === 0) return null;
  return entries.map(([k, v]) => {
    if (v && typeof v === "object" && "from" in v && "to" in v) {
      return (
        <div key={k} className="text-xs">
          <span className="font-mono text-slate-500">{k}</span>:{" "}
          <span className="line-through text-red-500">{String(v.from ?? "—")}</span>{" "}
          <span className="text-slate-400">→</span>{" "}
          <span className="text-emerald-700 font-medium">{String(v.to ?? "—")}</span>
        </div>
      );
    }
    return (
      <div key={k} className="text-xs">
        <span className="font-mono text-slate-500">{k}</span>:{" "}
        <span className="text-slate-700">{typeof v === "string" ? v : JSON.stringify(v)}</span>
      </div>
    );
  });
};

const AuditLogPanel = () => {
  const { authAxios, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const isSuperadmin = user && user.role === "superadmin";

  const load = () => {
    setLoading(true);
    authAxios
      .get("/admin/audit-logs?limit=200")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat audit log"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isSuperadmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin]);

  if (!isSuperadmin) {
    return (
      <div
        data-testid="admin-audit-forbidden"
        className="p-8 rounded-3xl bg-amber-50 border border-amber-200 text-amber-800"
      >
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-5 w-5" />
          <div className="font-heading font-semibold">Akses Terbatas</div>
        </div>
        <div className="text-sm">Audit log hanya dapat diakses oleh <b>superadmin</b>.</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-panel-audit-logs">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ScrollText className="h-6 w-6 text-blue-800" />
            Audit Log
          </h2>
          <p className="text-sm text-slate-500">
            Riwayat aksi create / update / delete pada akun admin.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="admin-audit-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-700"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="p-8 flex items-center justify-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-slate-400 border border-dashed border-slate-200 rounded-2xl">
          Belum ada aktivitas tercatat.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((log) => {
            const meta = ACTION_META[log.action] || {
              label: log.action,
              icon: ScrollText,
              className: "bg-slate-50 text-slate-700 border-slate-200",
            };
            const ActionIcon = meta.icon;
            return (
              <div
                key={log.id}
                data-testid={`audit-log-row-${log.id}`}
                className="bg-white rounded-2xl border border-slate-100 p-4 flex flex-col md:flex-row gap-3 md:items-center"
              >
                <div className="md:w-40 shrink-0">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[11px] font-medium ${meta.className}`}
                  >
                    <ActionIcon className="h-3 w-3" />
                    {meta.label}
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-sm text-slate-800 flex-wrap">
                    <UserIcon className="h-3.5 w-3.5 text-slate-400" />
                    <span className="font-medium">{log.actor_email || "—"}</span>
                    <span className="text-slate-400">({log.actor_role || "?"})</span>
                    <span className="text-slate-400">→</span>
                    <span className="font-mono text-xs text-slate-600 truncate">
                      {log.target_label || log.target_id || "—"}
                    </span>
                  </div>
                  <div className="mt-1 space-y-0.5">{formatChanges(log.changes)}</div>
                </div>

                <div className="md:w-52 shrink-0 text-xs text-slate-400 md:text-right">
                  <div>{log.created_at ? new Date(log.created_at).toLocaleString("id-ID") : "—"}</div>
                  {log.ip && <div className="font-mono">{log.ip}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AuditLogPanel;
